class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.cachedQueries = {};
    this.queryOverride = null;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.isOpen = false;

    this.setupEventListeners();
    this.onViewportChange = () => {
      if (this.isOpen)
        this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    };
  }

  connectedCallback() {
    window.addEventListener("resize", this.onViewportChange);
    if (
      this.dataset.recoverPrefix === "true" &&
      /^\d{3,18}$/.test(this.getQuery())
    )
      this.recoverPrefixSearch();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.onViewportChange);
    this.abortController?.abort();
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");

    if (!form) {
      return;
    }

    form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this),
    );

    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this),
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  async recoverPrefixSearch() {
    const term = this.getQuery();
    this.suppressRender = true;
    try {
      await this.getSearchResults(term);
      const key = this.queryKeyFor(term);
      if (
        this.isConnected &&
        this.getQuery() === term &&
        this.queryOverride?.key === key
      ) {
        const target = new URL(window.location.href);
        target.searchParams.set("q", this.queryOverride.query);
        target.searchParams.set("options[prefix]", "last");
        // Native GET results keep their actual filters, sort, counts and pagination.
        window.location.replace(target.href);
      }
    } finally {
      this.suppressRender = false;
      this.removeAttribute("loading");
    }
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.currentQueryKey = null;
      this.close(true);
      this.removeAttribute("loading");
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    // Enter on an active option is handled in onKeydown/onKeyup. A deliberate
    // click on either search button must still submit the full native search.
    const term = this.getQuery();
    if (!term.length) {
      event.preventDefault();
      return;
    }
    const key = this.queryKeyFor(term);
    if (this.allowNativeSubmit && this.queryOverride?.key !== key) return;
    if (this.queryOverride?.key === key) {
      // This was a verified native item-prefix query, not an invented result set.
      // SABER LOCAL SEARCH ADDITION START: preserve the original phrase on native GET.
      if (this.queryOverride.local)
        window.SaberSearchEnhancements?.markLocalForm(
          this.querySelector("form.search"), term, this.queryOverride.query,
        );
      // SABER LOCAL SEARCH ADDITION END
      this.input.value = this.queryOverride.query;
      return;
    }
    const helper = window.SaberCatalogSearch;
    if (
      helper?.prefixQueries(term).length &&
      !this.cachedResults[key] &&
      !this.submittingSearch
    ) {
      event.preventDefault();
      this.submittingSearch = true;
      const submitter = event.submitter;
      Promise.race([
        this.getSearchResults(term),
        new Promise((resolve) => setTimeout(resolve, 1800)),
      ]).finally(() => {
        this.submittingSearch = false;
        if (!this.isConnected || this.getQuery() !== term) return;
        this.allowNativeSubmit = true;
        const form = this.querySelector("form.search");
        if (submitter?.form === form) form.requestSubmit(submitter);
        else form.requestSubmit();
        this.allowNativeSubmit = false;
      });
    } else if (this.submittingSearch) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      return;
    }

    // Reuse only the cache for the current query, never a previous term's rendered panel.
    this.getSearchResults(searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.close();
      }
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.close(true);
    }

    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
      case "Escape":
        this.close();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === "ArrowUp" ||
      event.code === "ArrowDown" ||
      (event.code === "Escape" && this.isOpen) ||
      (event.code === "Enter" && this.querySelector('[aria-selected="true"]'))
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) {
      return;
    }

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('[role="option"]'),
    ).filter((element) => element.offsetParent !== null);
    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    if (selectedElement && selectedElementIndex === -1) {
      selectedElement.setAttribute("aria-selected", false);
      this.input.setAttribute("aria-activedescendant", "");
    }
    if (!allVisibleElements.length || (moveUp && selectedElementIndex === -1))
      return;

    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    if (this.statusElement) this.statusElement.textContent = "";

    const activeElementIndex =
      (selectedElementIndex + (moveUp ? -1 : 1) + allVisibleElements.length) %
      allVisibleElements.length;
    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
    activeElement.scrollIntoView({ block: "nearest" });
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) {
      selectedProduct.click();
    }
  }

  queryKeyFor(searchTerm) {
    return JSON.stringify([
      searchTerm.toLowerCase(),
      this.getSearchFilterType(),
      this.getSearchFilterTypeQuery().toLowerCase(),
    ]);
  }

  async fetchSection(searchTerm, signal) {
    const filterType = this.getSearchFilterType();
    const filterQuery = this.getSearchFilterTypeQuery();
    const params = new URLSearchParams({
      q: filterQuery
        ? `${filterType}:${filterQuery}+* ${searchTerm}`
        : searchTerm,
      "resources[limit_scope]": this.dataset.limitScope || "all",
      "resources[limit]": this.dataset.limit || "5",
      "resources[options][fields]":
        "title,body,product_type,tag,variants.title,variants.sku,vendor",
      "options[prefix]": "last",
      section_id: "predictive-search",
    });
    const response = await fetch(`${routes.predictive_search_url}?${params}`, {
      signal,
    });
    if (!response.ok) throw new Error("Suggestions unavailable");
    const section = new DOMParser()
      .parseFromString(await response.text(), "text/html")
      .querySelector("#shopify-section-predictive-search");
    if (!section) throw new Error("Missing predictive search section");
    return section;
  }

  verifiedPrefixProducts(section, searchTerm) {
    const helper = window.SaberCatalogSearch;
    return [...section.querySelectorAll("[data-search-product-id]")].filter(
      (node) => {
        try {
          const skus = JSON.parse(node.dataset.searchSkus || "[]");
          return (
            Array.isArray(skus) &&
            skus.some((sku) => helper.isSkuPrefixMatch(sku, searchTerm))
          );
        } catch (_) {
          return false;
        }
      },
    );
  }

  preparePrefixSection(section, nodes, searchTerm) {
    section
      .querySelectorAll(".predictive-search-results-group-section")
      .forEach((group) => {
        if (
          !group.querySelector("#predictive-search-results-item-product-list")
        )
          group.remove();
      });
    section
      .querySelector("#predictive-search-results-item-product-list")
      .replaceChildren(...nodes);
    section
      .querySelector(".predictive-search-results-group-wrapper")
      ?.classList.remove("predictive-search-results-group-wrapper-two-column");
    section
      .querySelector(".predictive-search-results-list")
      ?.classList.remove("predictive-search-results-list-empty");
    const button = section.querySelector(".predictive-search-item-term");
    if (button) button.textContent = `View items starting with ${searchTerm}`;
    const count = section.querySelector(
      "[data-predictive-search-live-region-count-value]",
    );
    if (count) count.textContent = `${nodes.length} matching item suggestions`;
    return section;
  }

  async getSearchResults(searchTerm) {
    const queryKey = this.queryKeyFor(searchTerm);
    this.currentQueryKey = queryKey;
    this.queryOverride = null;
    this.setLiveRegionLoadingState();
    if (this.cachedResults[queryKey]) {
      const query = this.cachedQueries[queryKey];
      if (query && query !== searchTerm)
        this.queryOverride = { key: queryKey, query };
      // SABER LOCAL SEARCH ADDITION START: mark a verified local alternative in the existing cache.
      if (this.queryOverride?.key === queryKey)
        this.queryOverride.local = window.SaberSearchEnhancements?.localAlternative(searchTerm) === query;
      // SABER LOCAL SEARCH ADDITION END
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }
    this.abortController?.abort();
    const controller = new AbortController();
    this.abortController = controller;
    const signal = controller.signal;
    try {
      let section = await this.fetchSection(searchTerm, signal);
      let resolvedQuery = searchTerm;
      const helper = window.SaberCatalogSearch;
      // At most two extra queries, only after a zero-product numeric query.
      // Both are literal label prefixes verified against this store's native search.
      if (
        helper &&
        !this.getSearchFilterTypeQuery() &&
        !section.querySelector(
          "[data-search-product-id], .predictive-search-list-item-product",
        )
      ) {
        for (const prefix of helper.prefixQueries(searchTerm)) {
          if (signal.aborted) return;
          const candidate = await this.fetchSection(prefix, signal);
          const matches = this.verifiedPrefixProducts(candidate, searchTerm);
          if (matches.length) {
            section = this.preparePrefixSection(candidate, matches, searchTerm);
            resolvedQuery = prefix;
            break;
          }
        }
      }
      // SABER LOCAL SEARCH ADDITION START: use a local phrase only after every native group is empty.
      // Keep every native hit and the existing SKU-prefix path. A complete,
      // curated local phrase is tried only when all native resource groups are empty.
      const alternative = window.SaberSearchEnhancements?.localAlternative(searchTerm);
      if (
        alternative && resolvedQuery === searchTerm && !this.getSearchFilterTypeQuery() &&
        !window.SaberSearchEnhancements?.literalRequested(searchTerm, window.location.href) &&
        !section.querySelector(
          "[data-search-product-id], .predictive-search-list-item-product, .predictive-search-list-item-query, .predictive-search-list-item-collection, .predictive-search-list-item-page, .predictive-search-list-item-article",
        )
      ) {
        try {
          const candidate = await this.fetchSection(alternative, signal);
          if (candidate.querySelector("[data-search-product-id], .predictive-search-list-item-product")) {
            section = candidate;
            resolvedQuery = alternative;
          }
        } catch (_) {
          if (signal.aborted) return;
          // A failed optional translation keeps the original native empty state.
        }
      }
      // SABER LOCAL SEARCH ADDITION END
      this.cachedResults[queryKey] = section.innerHTML;
      this.cachedQueries[queryKey] = resolvedQuery;
      if (
        this.currentQueryKey === queryKey &&
        this.abortController === controller
      ) {
        if (resolvedQuery !== searchTerm)
          this.queryOverride = { key: queryKey, query: resolvedQuery };
        // SABER LOCAL SEARCH ADDITION START: label the verified native query without changing prefix behavior.
        if (this.queryOverride?.key === queryKey)
          this.queryOverride.local = window.SaberSearchEnhancements?.localAlternative(searchTerm) === resolvedQuery;
        // SABER LOCAL SEARCH ADDITION END
        if (!this.suppressRender) this.renderSearchResults(section.innerHTML);
      }
    } catch (_) {
      if (
        this.currentQueryKey !== queryKey ||
        this.abortController !== controller
      )
        return;
      this.removeAttribute("loading");
      this.removeAttribute("results");
      this.close();
      // A failed suggestion request never removes the native GET form.
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent,
    );
  }

  getResultsMaxHeight() {
    const anchor =
      this.querySelector(".header-search-global-form-wrap") || this.input;
    const bottom = anchor.getBoundingClientRect().bottom;
    this.resultsMaxHeight = Math.max(
      0,
      Math.min(560, window.innerHeight - bottom - 12),
    );
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);

    if (!this.isOpen) {
      const event = new CustomEvent("search:open");
      document.dispatchEvent(event);
    }

    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    // Keep any pending response cacheable, but do not let it reopen a dismissed panel.
    this.currentQueryKey = null;
    this.removeAttribute("loading");
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) {
      selected.setAttribute("aria-selected", false);
    }

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");

    if (this.isOpen) {
      const event = new CustomEvent("search:close");
      document.dispatchEvent(event);
    }

    this.isOpen = false;
  }
}

customElements.define("predictive-search", PredictiveSearch);
